import { Component, useCallback, useEffect, useMemo, useRef } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";
import type { CockpitTarget, TileCount, TileData, TileDefinition, TileFact, TileSize, Unternehmen } from "@kundeninfo/api-client";
import { useSofortPopup } from "@kundeninfo/ui";
import { useDiagramm, useDiagrammThema } from "@kundeninfo/ui/diagramm";
import type { DiagrammThema, EchartsInstanz } from "@kundeninfo/ui/diagramm";
import { Chip, Hinweis, Karte, Listenkachel, Objektsymbol } from "@kundeninfo/ui/me-design";
import type { ListenkachelKennzahl, Objektart } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { relativeZeit } from "../../format.js";
import { companyOf } from "../customerOverview/texts.js";
import { LONG_PRESS_MS, LONG_PRESS_SLOP_PX } from "../tickets/TicketTimelineChart.js";
import {
  a11yName, a11yRows, bucketAt, bucketSpan, chartOption, deltaText, deltaTone, displaySeries, GRID, isSignedSeries, popupDelta, popupRows,
  seriesTotal, tileColor,
} from "./chartOption.js";
import type { ChartInput, PopupRow } from "./chartOption.js";
import { COCKPIT_TEXT_WEB as T } from "./cockpitTexts.js";
import type { TileChoice } from "./tileChoice.js";
import "./Cockpit.css";

/**
 * DER KACHELRAHMEN (Spec „Kachelrahmen und Darstellungsarten“, „Laden, Fehler, Neuladen“). Er kennt keine einzelne Kachel: `COUNT` und
 * `LIST` zeichnet die Listenkachel, `SERIES` und `STACKED` eine Karte mit Zeichenfläche. Die Wurzel bleibt über alle Zustände dieselbe,
 * so trägt sie Kennung, Größe und „ausgeblendet“ auch für die Karte, deren Hülle keine fremden Attribute durchreicht. Neue Antworten
 * gehen über `setOption` an dieselbe Instanz, Rollposition und Fokus bleiben.
 */

export type TileState = { status: "laedt" | "ok" | "fehler"; data: TileData | null; stale: boolean };
export type CockpitTileProps = {
  definition: TileDefinition; choice: TileChoice; state: TileState; hidden: boolean;
  companies: readonly Unternehmen[];
  /** Listenzeilen nennen ihr Unternehmen, wenn die Wand mehrere zeigt. Im Filter auf eines stünde überall dasselbe. */
  showCompany: boolean;
  phone: boolean;
  onRetry: () => void; onTarget: (t: CockpitTarget) => void; onDrill: (bucketStart: string) => void;
};

/** So viele Listenzeilen zeigt eine Größe; der Server liefert immer bis zu 50, die Größe ändert sich ohne neuen Abruf. */
export const LIST_ROWS: Readonly<Record<TileSize, number>> = { "1x1": 5, "2x1": 5, "2x2": 12 };
const MAX_FACTS = 4;

const LEVEL_ART = { ERROR: "fehler", WARN: "warnung" } as const;
/** Schlechtestes oben: Fehler, Warnung, dann der Rest in der Folge des Servers. */
const LEVEL_RANK = { ERROR: 0, WARN: 1 } as const;
const rankOf = (level: TileCount["level"]): number => (level === null ? 2 : LEVEL_RANK[level]);

const toneClass = (tone: "gut" | "schlecht" | null): string => (tone === null ? "" : ` ck__delta--${tone}`);
const swatchStyle = (color: string): CSSProperties => ({ "--ck-color": color } as CSSProperties);

/** Das Farbfeld im Steckplatz `media` eines Legenden-Chips, nie dessen Host; ohne Farbe die gestrichelte Vergleichslinie. */
function Swatch(p: { color?: string }): ReactElement {
  return p.color === undefined ? <i className="ck__swatch ck__swatch--compare" /> : <i className="ck__swatch" style={swatchStyle(p.color)} />;
}

// ── Zahlen und Liste ──────────────────────────────────────────────────────────────────────────────────────────

/** Eine Zeile der Listenkachel: `data` sind ihre Angaben, auch das Δ (`data-unter-wert`, `data-unter-ton`, C6). */
type Row = { id: string; text: string; data: Record<string, string>; target: CockpitTarget | null };

const present = (o: Record<string, string | null>): Record<string, string> =>
  Object.fromEntries(Object.entries(o).filter((e): e is [string, string] => e[1] !== null));

const underText = (parts: readonly (string | null)[]): string | null => {
  const known = parts.filter((p) => p !== null);
  return known.length === 0 ? null : T.tile.parts(known);
};

type CountLike = Omit<TileCount, "value"> & { value: number | null };

const hasValues = (data: TileData): boolean => data.series.some((s) => s.values.some((v) => v !== null));

/**
 * Der Leertext einer Kachel ohne Werte, in jeder Darstellung derselbe Weg. Meldet die Antwort `MISSING_SNAPSHOTS`, fehlt noch jeder
 * Schnappschuss: Das sagt die Kachel statt „Keine Werte“. Ohne Warnung bleibt es beim Bestand (RF4), die Liste hat ihren eigenen Satz.
 */
function emptyHint(data: TileData, isList: boolean): string {
  if (isList) return T.tile.noRows;
  return data.warnings.some((w) => w.code === "MISSING_SNAPSHOTS") ? T.tile.noSnapshot : T.tile.noValues;
}

/** Die Zahlen: `counts`, ohne sie die Summen der Reihen. Vorzeichenbehaftete Reihen sind nie Quelle einer Kennzahl (645). */
function countsOf(data: TileData, definition: TileDefinition): CountLike[] {
  if (data.counts.length > 0) return data.counts;
  // Ohne einen einzigen Wert gibt es auch keine Summe: Die Kachel zeigt ihren Leertext wie das Diagramm, keine Zeilen aus „–“.
  if (!hasValues(data)) return [];
  return data.series.filter((s) => !isSignedSeries(s)).map((s) => ({ key: s.key, label: s.label, value: seriesTotal(s), unit: definition.unit,
    compareValue: null, level: null, detail: null, target: null }));
}

function countRows(data: TileData, definition: TileDefinition): Row[] {
  const mode = data.compare?.mode;
  return [...countsOf(data, definition)].sort((a, b) => rankOf(a.level) - rankOf(b.level)).map((c) => {
    const line = mode === undefined || c.compareValue === null ? null : T.tile.compareLine(mode, T.valueText(c.compareValue, c.unit, data.currency));
    const delta = line === null ? null : deltaText(c.value, c.compareValue, c.unit, data.currency);
    // Wert und Ton setzt das Element hinter den Unter-Text, mit eigenem Trenner; gekürzt wird nur der Text davor (C6).
    return { id: c.key, text: c.label, target: c.target,
      data: present({
        "data-zusatz": c.value === null ? T.popup.noValue : T.valueText(c.value, c.unit, data.currency),
        "data-unter": underText([c.detail, line]),
        "data-unter-wert": delta,
        "data-unter-ton": delta === null ? null : deltaTone(c.value! - c.compareValue!, c.better === undefined ? definition.better : c.better),
        "data-status-art": c.level === null ? null : LEVEL_ART[c.level], "data-status": c.level === null ? null : c.label }) };
  });
}

/** Das Kennzeichen eines Unternehmens als Kürzel in der Hausfarbe, wie an den Zeilen der Kundenübersicht. */
function companyTag(id: string, companies: readonly Unternehmen[]): string {
  const { code, name, color } = companyOf(id, companies);
  return JSON.stringify([{ kuerzel: code, name, ...(color === undefined ? {} : { farbe: color }) }]);
}

/** Der Unter-Text kürzt sich am Ende: Die Zeit steht vorn, sonst fiele sie bei einem langen Titel oder Partnernamen weg (gemessen in 1×1 und am Telefon). */
function listRows(data: TileData, size: TileSize, tagCompany: (id: string) => string | null): Row[] {
  return data.rows.slice(0, LIST_ROWS[size]).map((r) => ({ id: r.id, text: T.activity(r.textKey, r.params, r.actor?.name ?? null), target: r.target,
    data: present({ "data-kennung": r.reference, "data-unter": underText([relativeZeit(r.at, new Date(data.now)), r.detail]),
      "data-kennzeichen": r.companyId === null ? null : tagCompany(r.companyId) }) }));
}

const factText = (f: TileFact, data: TileData): string => (f.value === null ? T.popup.noValue : T.valueText(f.value, f.unit, data.currency));

/** Die Kennzahlenzeile der Listenkachel: Was schon Zeile ist, steht nicht noch einmal darüber. */
function factsOf(data: TileData, rows: readonly Row[]): ListenkachelKennzahl[] {
  const inRows = new Set(rows.map((r) => r.id));
  return data.facts.filter((f) => !inRows.has(f.key)).slice(0, MAX_FACTS).map((f) => ({ beschriftung: f.label, wert: factText(f, data),
    ...(f.level === null ? {} : { statusArt: LEVEL_ART[f.level], status: f.label }) }));
}

/**
 * Leise unter dem Inhalt, in jeder Darstellung. Ein unbekannter Code (ältere Oberfläche gegen neueren Server) fällt ohne Text weg,
 * vorwärtsverträglich wie `textKey`. `clipped`: Die Zeile im Diagramm kürzt sich, das Sofort-Popup trägt dann den vollen Text.
 */
function Warnings({ data, clipped }: { data: TileData; clipped: boolean }): ReactElement | null {
  const text = T.tile.parts(data.warnings.flatMap((w) => (Object.hasOwn(T.warnings, w.code) ? [T.warnings[w.code](w.count)] : [])));
  const popup = useSofortPopup<HTMLParagraphElement>(clipped ? text : "");
  return text === "" ? null : <p ref={popup} className="ck__warnings">{text}</p>;
}

function ListFrame(p: CockpitTileProps): ReactElement {
  const { definition, choice, state } = p;
  const jump = (target: CockpitTarget | null): void => {
    const to = target ?? definition.target;
    if (to !== null) p.onTarget(to);
  };
  // Ohne Ziel der Kachel (Letzte Aktionen) ist der Kopf kein Knopf, ohne Pfeil und Rahmen; nur die Zeilen springen (C7, C9).
  const common = { titel: definition.title, art: definition.icon as Objektart, wert: definition.tileKey, ohneZiel: definition.target === null,
    onGewaehlt: () => { jump(null); } };
  if (state.status === "fehler") return <Listenkachel {...common} fehler={T.tileFailed} onErneut={() => { p.onRetry(); }} />;
  const { data } = state;
  if (data === null) return <Listenkachel {...common} leer={T.tile.loading} />;
  const isList = choice.display === "LIST";
  const rows = isList ? listRows(data, choice.size, (id) => (p.showCompany ? companyTag(id, p.companies) : null)) : countRows(data, definition);
  const more = isList && definition.target !== null ? data.rows.length - rows.length : 0;
  return (
    <>
      <Listenkachel {...common} leer={emptyHint(data, isList)} kennzahlen={factsOf(data, rows)} {...(more > 0 ? { weitere: more } : {})}
        onZeileGewaehlt={(d) => { jump(rows[d.index]?.target ?? null); }}>
        {rows.map((r) => <option key={r.id} value={r.id} {...r.data}>{r.text}</option>)}
      </Listenkachel>
      <Warnings data={data} clipped={false} />
    </>
  );
}

// ── Popup und Band ────────────────────────────────────────────────────────────────────────────────────────────

const GRID_0 = { gridIndex: 0 };
const POPUP_GAP_PX = 14, POPUP_ABOVE_PX = 10, POPUP_MARGIN_PX = 6, BAND_MIN_PX = 2;
type Point = { clientX: number; clientY: number };

/** Eine Popup-Zeile: Text nur als Textknoten (Muster Zeitachse), die Marke in der Farbe der Reihe, der Wert in eigener Spalte rechts. */
function popupLine({ label, value }: PopupRow, className: string, color: string | null): HTMLElement {
  const line = document.createElement("div");
  line.className = className;
  if (color !== null) {
    const mark = document.createElement("i");
    mark.className = "ck__swatch";
    mark.style.setProperty("--ck-color", color);
    line.append(mark);
  }
  line.append(label);
  if (value !== null) {
    const cell = document.createElement("span");
    cell.className = "ck__popup-value";
    cell.append(value);
    line.append(cell);
  }
  return line;
}

/** Zeile 0 ist der Abschnitt, Zeile `k + 1` gehört zu `data.series[k]`, danach folgen Summe und Vergleich, zuletzt gegebenenfalls das Δ. */
function fillPopup(node: HTMLElement, i: ChartInput, index: number, thema: DiagrammThema): void {
  const { series } = i.data;
  const lines = popupRows(i, index);
  const delta = popupDelta(i, index);
  node.replaceChildren(...lines.map((row, k) => {
    if (k === 0) return popupLine(row, "ck__popup-head", null);
    const s = series[k - 1];
    const toned = delta !== null && k === lines.length - 1 ? ` ck__delta${toneClass(deltaTone(delta, i.definition.better))}` : "";
    const rule = k === series.length + 1 ? " ck__popup-rule" : "";
    return popupLine(row, `ck__popup-line${rule}${toned}`, s === undefined ? null : tileColor(s.color, thema, i.companies));
  }));
}

function placePopup(node: HTMLElement, { clientX, clientY }: Point): void {
  const width = node.offsetWidth, height = node.offsetHeight;
  node.style.left = `${Math.max(POPUP_MARGIN_PX, Math.min(clientX + POPUP_GAP_PX, window.innerWidth - width - POPUP_MARGIN_PX))}px`;
  node.style.top = `${clientY + POPUP_GAP_PX + height < window.innerHeight ? clientY + POPUP_GAP_PX : Math.max(POPUP_MARGIN_PX, clientY - height - POPUP_ABOVE_PX)}px`;
}

const pixelOf = (instanz: EchartsInstanz, ms: number): number => instanz.convertToPixel(GRID_0, [ms, 0])[0]!;

function placeBand(node: HTMLElement, instanz: EchartsInstanz, span: [number, number]): void {
  const left = pixelOf(instanz, span[0]);
  node.style.left = `${left}px`;
  node.style.width = `${Math.max(BAND_MIN_PX, pixelOf(instanz, span[1]) - left)}px`;
  node.style.top = `${GRID.top}px`;
  node.style.bottom = `${GRID.bottom}px`;
  node.hidden = false;
}

/**
 * Der Zeiger über der Zeichenfläche. Popup und Band sind eigene Knoten und gehen an React vorbei: Jede Mausbewegung bliebe sonst ein Neuzeichnen.
 * Der Abschnitt folgt aus der Stelle (K24), nicht aus der nächsten Stützstelle. Am Telefon und bei jeder Berührung öffnet der Langdruck,
 * Antippen springt nicht; die Maus fährt, ein Klick geht in den Monat.
 */
function useChartPointer(i: ChartInput | null, instanz: EchartsInstanz | null, thema: DiagrammThema, phone: boolean, onDrill: (start: string) => void) {
  const surface = useRef<HTMLDivElement | null>(null);
  const band = useRef<HTMLDivElement | null>(null);
  const popup = useRef<HTMLDivElement | null>(null);
  const press = useRef<{ x: number; y: number; timer: ReturnType<typeof setTimeout> } | null>(null);
  const touched = useRef(false);

  const hide = useCallback((): void => {
    if (popup.current !== null) popup.current.hidden = true;
    if (band.current !== null) band.current.hidden = true;
  }, []);
  const stopPress = useCallback((): void => {
    if (press.current !== null) clearTimeout(press.current.timer);
    press.current = null;
  }, []);
  useEffect(() => {
    hide();
    stopPress();
  }, [i, hide, stopPress]);
  useEffect(() => {
    window.addEventListener("scroll", hide, true);
    window.addEventListener("pointerdown", hide, true);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("pointerdown", hide, true);
      stopPress();
    };
  }, [hide, stopPress]);

  const bucketUnder = (e: Point): number => {
    if (i === null || instanz === null || surface.current === null) return -1;
    const box = surface.current.getBoundingClientRect();
    const pixel = [e.clientX - box.left, e.clientY - box.top];
    return instanz.containPixel(GRID_0, pixel) ? bucketAt(i.data.buckets, instanz.convertFromPixel(GRID_0, pixel)[0]!) : -1;
  };
  const show = (e: Point): void => {
    const index = bucketUnder(e);
    if (index < 0 || i === null || instanz === null || popup.current === null || band.current === null) { hide(); return; }
    placeBand(band.current, instanz, bucketSpan(i.data.buckets[index]!));
    fillPopup(popup.current, i, index, thema);
    popup.current.hidden = false;
    placePopup(popup.current, e);
  };
  const byTouch = (e: ReactPointerEvent): boolean => phone || e.pointerType === "touch";

  return { surface, band, popup, handlers: {
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>): void => {
      touched.current = byTouch(e);
      if (!touched.current) return;
      stopPress();
      const point = { clientX: e.clientX, clientY: e.clientY };
      press.current = { x: e.clientX, y: e.clientY, timer: setTimeout(() => { press.current = null; show(point); }, LONG_PRESS_MS) };
    },
    onPointerMove: (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (!byTouch(e)) { show(e); return; }
      const s = press.current;
      if (s !== null && Math.hypot(e.clientX - s.x, e.clientY - s.y) > LONG_PRESS_SLOP_PX) stopPress();
    },
    onPointerUp: stopPress,
    onPointerCancel: stopPress,
    // Ein Finger verlässt die Fläche beim Loslassen, das Popup des Langdrucks bleibt bis zum nächsten Drücken.
    onPointerLeave: (e: ReactPointerEvent<HTMLDivElement>): void => { if (!byTouch(e)) hide(); },
    onClick: (e: Point): void => {
      if (phone || touched.current || i?.data.grain !== "MONTH") return;
      const index = bucketUnder(e);
      if (index >= 0) onDrill(i.data.buckets[index]!.start);
    },
  } };
}

// ── Diagramm ──────────────────────────────────────────────────────────────────────────────────────────────────

/** Oben rechts als rahmenlose Kennzeichen, alle Reihen auch bei 0, in den Farben der Zeichnung. Zeichnet SERIES die Summe, steht ein Eintrag in deren Farbe. */
function Legend({ i, thema }: { i: ChartInput; thema: DiagrammThema }): ReactElement {
  const { data, display, companies } = i;
  const drawn = displaySeries(data, display);
  const entries = drawn.length < data.series.length ? [{ ...drawn[0]!, label: T.tile.sumOf(data.series.map((s) => s.label)) }] : drawn;
  return (
    <div className="ck__legende">
      {entries.map((s) => (
        <Chip key={s.key} media={<Swatch color={tileColor(s.color, thema, companies)} />}>{s.label}</Chip>))}
      {data.compare === null ? null : <Chip media={<Swatch />}>{T.popup.compareModes[data.compare.mode]}</Chip>}
    </div>
  );
}

function FactLine({ data }: { data: TileData }): ReactElement | null {
  if (data.facts.length === 0) return null;
  return (
    <dl className="ck__facts">
      {data.facts.slice(0, MAX_FACTS).map((f) => (
        <div key={f.key} className={f.level === null ? "ck__fact" : `ck__fact ck__fact--${f.level.toLowerCase()}`}>
          <dt>{f.label}</dt><dd>{factText(f, data)}</dd>
        </div>))}
    </dl>
  );
}

function A11yTable({ rows, name }: { rows: readonly string[][]; name: string }): ReactElement {
  const [head = [], ...body] = rows;
  return (
    <table className="ck__tabelle" aria-label={name}>
      <thead><tr>{head.map((c, k) => <th key={k} scope="col">{c}</th>)}</tr></thead>
      <tbody>{body.map((row, r) => (
        <tr key={r}>{row.map((c, k) => (k === 0 ? <th key={k} scope="row">{c}</th> : <td key={k}>{c}</td>))}</tr>))}
      </tbody>
    </table>
  );
}

function ChartFrame(p: CockpitTileProps & { display: "SERIES" | "STACKED" }): ReactElement {
  const { definition, display, state, companies, phone } = p;
  const { data } = state;
  const thema = useDiagrammThema();
  const input = useMemo<ChartInput | null>(() => (data === null ? null : { data, definition, display, companies, phone }),
    [data, definition, display, companies, phone]);
  const option = useMemo(() => (input === null ? null : chartOption(input, thema)), [input, thema]);
  const rows = useMemo(() => (input === null ? [] : a11yRows(input)), [input]);
  const chart = useDiagramm(option);
  const pointer = useChartPointer(input, chart.instanz, thema, phone, p.onDrill);
  const failed = state.status === "fehler";
  const shown = failed ? null : input;
  // Eine Reihe aus lauter 0 ist eine Aussage und bleibt ohne Hinweis.
  const hint = failed ? null : input === null ? T.tile.loading : hasValues(input.data) ? null : emptyHint(input.data, false);
  const target = definition.target;
  const head: ReactNode = (
    <div className="ck__head">
      <button type="button" className="ck__title" disabled={target === null} onClick={() => { if (target !== null) p.onTarget(target); }}>
        <Objektsymbol art={definition.icon as Objektart} groesse={16} />
        <span className="ck__title-text">{definition.title}</span>
        <svg className="ck__arrow" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3.5 10.5 8 6 12.5" /></svg>
      </button>
      {shown === null || phone ? null : <Legend i={shown} thema={thema} />}
    </div>
  );
  return (
    <>
      <Karte className="ck__card" titel={head}>
        {failed ? <Hinweis art="fehler" aktionen={<KiKnopf kompakt onClick={() => { p.onRetry(); }}>{T.reload}</KiKnopf>}>{T.tileFailed}</Hinweis> : null}
        {shown === null ? null : <FactLine data={shown.data} />}
        <div ref={pointer.surface} className="ck__surface" role="img" aria-label={input === null ? T.tile.loadingName(definition.title) : a11yName(input)} hidden={failed}
          {...(input?.data.grain === "MONTH" && !phone ? { "data-drill": "" } : {})} {...pointer.handlers}>
          <div ref={chart.ref} className="ck__canvas" />
          <div ref={pointer.band} className="ck__band" aria-hidden="true" hidden />
          {hint === null ? null : <p className="ck__empty">{hint}</p>}
        </div>
        {shown === null ? null : <A11yTable rows={rows} name={definition.title} />}
        {shown === null ? null : <Warnings data={shown.data} clipped />}
      </Karte>
      {createPortal(<div ref={pointer.popup} className="ck__popup" role="tooltip" hidden />, document.body)}
    </>
  );
}

// ── Wurzel ────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Eine Kachel fällt für sich aus (Spec „Kernaussage“), auch beim Zeichnen: Die Anwendung hat keine Fehlergrenze, und die Wand ist die Startseite. Ein Wurf
 * nähme sonst Kopfleiste und Weg hinaus mit. Jede neue Antwort (`attempt`) versucht es wieder.
 */
class TileBoundary extends Component<{ attempt: unknown; fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  override componentDidUpdate(before: { attempt: unknown }): void {
    if (this.state.failed && before.attempt !== this.props.attempt) this.setState({ failed: false });
  }
  override render(): ReactNode { return this.state.failed ? this.props.fallback : this.props.children; }
}

export function CockpitTile(p: CockpitTileProps): ReactElement {
  const { definition, choice, state } = p;
  const { display } = choice;
  // Ausgeblendet trägt die Wurzel nur Kennung und Namen für die Wand: kein Abruf, also auch keine Zeichenfläche und kein ECharts-Chunk.
  return (
    <div id={`ck-tile-${definition.tileKey}`} className={state.stale ? "ck__tile ck__tile--stale" : "ck__tile"} data-groesse={choice.size}
      data-name={definition.title} {...(p.hidden ? { ausgeblendet: "" } : {})}>
      {p.hidden ? null : (
        <TileBoundary attempt={state} fallback={<Hinweis art="fehler" aktionen={<KiKnopf kompakt onClick={() => { p.onRetry(); }}>{T.reload}</KiKnopf>}>{T.tileFailed}</Hinweis>}>
          {display === "SERIES" || display === "STACKED" ? <ChartFrame {...p} display={display} /> : <ListFrame {...p} />}
        </TileBoundary>
      )}
    </div>
  );
}
